import { useState } from 'react';
import {
  useDownloadContractMutation,
  useAddContractCommentMutation,
  useRevokeContractMutation,
  useTerminateContractMutation,
  useSetMvdNotifiedMutation,
  useSetMvdNotifiedOnTerminationMutation,
} from 'redux/Api/contractsApi/contractsApi';
import { ContractType } from 'redux/Api/contractsApi/type';

export const useContractActions = (contract: ContractType) => {
  const [opened, setOpened] = useState(false);
  const [isMvdNotified, setIsMvdNotified] = useState(contract.is_mvd_notified);
  const [isMvdNotifiedOnTermination, setIsMvdNotifiedOnTermination] = useState(
    contract.is_mvd_notified_on_termination,
  );
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [showTerminateModalWithOrders, setShowTerminateModalWithOrders] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);

  const [isDownloadingContract, setIsDownloadingContract] = useState(false);
  const [downloadContract] = useDownloadContractMutation();
  const [addContractComment] = useAddContractCommentMutation();
  const [revokeContract] = useRevokeContractMutation();
  const [terminateContract] = useTerminateContractMutation();
  const [setMvdNotified] = useSetMvdNotifiedMutation();
  const [setMvdNotifiedOnTermination] = useSetMvdNotifiedOnTerminationMutation();

  const handleDownloadContract = async () => {
    setIsDownloadingContract(true);
    try {
      const contractId =
        typeof contract.id === 'string' ? parseInt(contract.id) : contract.id;
      const blob = await downloadContract(contractId).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `unified_contract_${contract.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('error', error);
    } finally {
      setIsDownloadingContract(false);
    }
  };

  const handleAddComment = () => {
    setShowCommentModal(true);
  };

  const confirmAddComment = (comment: string) => {
    if (comment.trim()) {
      const contractId =
        typeof contract.id === 'string' ? parseInt(contract.id) : contract.id;
      addContractComment({ contractId, comment })
        .unwrap()
        .catch((error) => {
          console.error('error', error);
        });
    }
  };

  const handleRevokeContract = () => {
    setShowRevokeModal(true);
  };

  const confirmRevokeContract = () => {
    const contractId =
      typeof contract.id === 'string' ? parseInt(contract.id) : contract.id;
    revokeContract(contractId)
      .unwrap()
      .catch((error) => {
        console.error('error', error);
      });
  };

  const confirmTerminateContract = () => {
    const contractId =
      typeof contract.id === 'string' ? parseInt(contract.id) : contract.id;
    terminateContract(contractId)
      .unwrap()
      .catch((error) => {
        console.error('error', error);
      });
  };

  const handleMvdNotificationToggle = async (termination?: boolean) => {
    try {
      const contractId =
        typeof contract.id === 'string' ? parseInt(contract.id) : contract.id;
      if (termination) {
        await setMvdNotifiedOnTermination(contractId).unwrap();
        setIsMvdNotifiedOnTermination(!isMvdNotifiedOnTermination);
      } else {
        await setMvdNotified(contractId).unwrap();
        setIsMvdNotified(!isMvdNotified);
      }
    } catch (error) {
      console.error('error', error);
    }
  };

  const handleItemClick = async (itemId: string) => {
    switch (itemId) {
      case 'download':
        await handleDownloadContract();
        break;
      case 'comment':
        handleAddComment();
        break;
      case 'revoke':
        handleRevokeContract();
        break;
      case 'terminate':
        if (contract?.active_orders_count > 0) {
          setShowTerminateModalWithOrders(true);
        } else {
          setShowTerminateModal(true);
        }

        break;
      default:
        break;
    }
    setOpened(false);
  };

  return {
    opened,
    setOpened,
    isDownloadingContract,
    isMvdNotified,
    isMvdNotifiedOnTermination,
    showRevokeModal,
    setShowRevokeModal,
    showTerminateModal,
    setShowTerminateModal,
    showTerminateModalWithOrders,
    setShowTerminateModalWithOrders,
    showCommentModal,
    setShowCommentModal,
    handleItemClick,
    handleMvdNotificationToggle,
    confirmRevokeContract,
    confirmTerminateContract,
    confirmAddComment,
  };
};
